/* Modulo: cart sync — the one engine every section uses to tell a theme its cart changed.
   ────────────────────────────────────────────────────────────────────────────
   WHY THIS FILE EXISTS

   Inbound merchant report, twice (2026-08-23, 2026-09-06). Both times: "the add
   works, but I can't see the products until I refresh the page." The first fix
   added a notifyCart() helper to nine sections and a test that pinned it. The
   test passed. The merchant stayed broken. That is the whole lesson here — a
   green suite beside a broken store means the model of the theme is wrong, not
   that the code is right.

   The model was wrong in one specific way: every hook we shipped was DAWN'S.

     · findDrawer()   probed five Dawn-shaped selectors.
     · sectionList()  fell back to five literal Dawn section names.
     · graftSections() looked up '#shopify-section-' + name.
     · notifyCart()   dispatched cart:refresh / cart:updated / cart:update and
                      published on Shopify.PubSub.

   The merchant is on Horizon 4.1.4, where every one of those is inert. Measured
   against their live storefront:

     · No element matches any of the five drawer selectors.
     · GET /products/…?sections=cart-drawer,cart-icon-bubble,cart-notification
       answers HTTP 200 {"cart-drawer":null,"cart-icon-bubble":null,
       "cart-notification":null} — the API returns null for a section that does
       not exist rather than erroring, so graftSections() skipped every key and
       the caller could not tell the refresh had done nothing.
     · Zero listeners for cart:refresh, cart:updated or cart:update anywhere in
       the theme, and window.Shopify.PubSub does not exist on the page.

   And it is not only Horizon. On DAWN ITSELF the graft is a guaranteed no-op:
   Dawn renders both cart surfaces as SNIPPETS (layout/theme.liquid
   `{%- render 'cart-drawer' -%}`, sections/header.liquid
   `{%- render 'cart-notification' -%}`), so #shopify-section-cart-drawer has
   never existed in a Dawn DOM. sections/cart-drawer.liquid exists only so the
   Section Rendering API can render it standalone. We were guessing a name that
   happened to be right and a wrapper that was always absent.

   ────────────────────────────────────────────────────────────────────────────
   WHAT THIS DOES INSTEAD

   Three families, tried in order, none of them guessing a name:

   1. DAWN FAMILY — ask the theme which sections IT wants.
      Dawn-derived themes expose getSectionsToRender() on their cart elements.
      We read the section names and the mount nodes out of that, so a fork that
      renamed its sections, or moved them into a section group with a dynamic
      id, still works. This is the same discovery Shopify's own storefront
      runtime performs, and it is the reason no literal section name appears
      anywhere below.

   2. HORIZON FAMILY — dispatch the event its components actually listen for.
      Horizon listens for exactly one thing, `shopify:cart:lines-update`
      (StandardEvents.cartLinesUpdate), and every listener is written
      `event.promise?.then(({ cart, detail }) => …)`. An event without a
      resolvable `promise` property is a silent no-op even when the NAME is
      right, which is why adding the name alone would not have fixed this.

      Read from the theme's shipped source, this one event drives all four
      surfaces that were stale:
        cart-icon             renderCartBubble(cart.totalQuantity)
        header-actions        the "N items" live region
        cart-drawer-component auto-open, gated on event.action === 'add'
        cart-items-component  re-renders its own section — it reads its own
                              section id off data-section-id and calls
                              sectionRenderer.renderSection(id, {cache:false}),
                              so we do not have to know that id either.

      cart-items-component gates on #isExternalCartUpdate(event), which is
      `!(event.target instanceof Node) || !this.contains(event.target)`. We
      dispatch on `document`, which no component contains, so it reads as
      external and re-renders. That is the drawer BODY — the thing the merchant
      screenshotted as empty.

      On dispatching a first-party event name ourselves: this is the one place
      the direction of the contract is bent. The events are documented as
      something the storefront dispatches and apps listen to. We dispatch it
      because the alternative is worse — the only sanctioned way to trigger the
      refresh is Shopify.actions.updateCart, and that PERFORMS the mutation, so
      calling it after our own /cart/add.js would add the item twice. The event
      we send is the honest description of what we just did (we updated cart
      lines), it is shaped exactly like the theme's own, and every listener that
      receives it is individually guarded. See open() for where we do use the
      first-party action, because there it costs nothing.

   3. LEGACY — the vocabulary we already shipped.
      cart:refresh / cart:updated / cart:update / Shopify.PubSub, kept verbatim
      and fired last. Some themes already work because of these, and this file
      must not regress a store that is fine today. cart:update now carries the
      exact detail shape Shopify's own runtime sends, rather than the one we
      invented.

   ────────────────────────────────────────────────────────────────────────────
   WHAT WE DELIBERATELY DID NOT CHANGE

   The MUTATION stays on /cart/add.js. Shopify.actions.updateCart is the newer,
   sanctioned path and it would be tempting to move the whole add onto it, but:

     · Its default implementation ends `U() && G() || W() && K() ||
       window.location.reload()`. On a Horizon storefront in cart-PAGE mode
       there is no cart-items-component on a product page, so U() is false, no
       Dawn element defines getSectionsToRender() so W() is false, and the
       sanctioned path RELOADS THE PAGE after every quick-add. Our path does
       not need to.
     · It speaks Storefront Cart GraphQL with `lines`/`attributes`, so a product
       form carrying properties[…], selling_plan, or fields an app injected
       would have to be translated field by field. /cart/add.js takes the form
       as-is and cannot drop anything.
     · Switching it would change behaviour for every merchant, including the
       ones for whom today's code works. The bug is the refresh, so the fix is
       the refresh.

   This engine also never asks for `sections` on the ADD. Shopify documents that
   an invalid `sections`/`sections_url` returns HTTP 400 for the whole request,
   "However, this doesn't mean that the rest of the request didn't succeed" —
   the item IS added while the caller sees a failure and skips every refresh
   step, which is the reported symptom wearing a different hat. Asking on the
   /cart.js read afterwards means a bad name costs a null instead of poisoning
   the add. The four sections that still send those parameters on their POST do
   so only for their own no-engine fallback path; nothing here reads them.
*/
(function () {
  'use strict';

  /* One engine per document. Several Modulo sections can be on one page and
     each ships this same <script src>; an identical src still executes once
     per tag, so the guard is what stops three copies racing three /cart.js
     reads on a single add. */
  if (window.ModuloCart) return;

  /* Markets: never hardcode '/cart.js'. window.Shopify.routes.root is the only
     value documented as always ending in a slash, and on a subfolder market a
     literal path reads the wrong currency. */
  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* Horizon is recognised by its COMPONENTS, the way Shopify's own
     standard-actions runtime does it (cart-icon.renderCartBubble +
     cart-items-component), never by Shopify.theme.schema_name, so a renamed
     fork still counts. Everything Horizon-shaped is driven by the one event in
     horizonNotify(): the theme morphs its own drawer from detail.sections and
     decides for itself whether to open it (cart-drawer-component honours the
     merchant's "auto-open" setting on our event exactly as on its own adds).
     Measured on two merchants' storefronts 2026-09-06: grafting into a Horizon
     wrapper ourselves and force-opening it are both wrong -- the first
     replaces live custom elements the theme's morph would have preserved, the
     second overrides a setting the merchant chose. */
  function isHorizonCart() {
    var ci = document.querySelector('cart-icon');
    return !!document.querySelector('cart-items-component') ||
      !!(ci && typeof ci.renderCartBubble === 'function');
  }

  function hostsHorizon(node) {
    try { return !!node.querySelector('cart-items-component,cart-drawer-component'); } catch (e) { return false; }
  }

  function debugOn() {
    try { return !!window.MODULO_DEBUG || (window.localStorage && localStorage.getItem('modulo_debug') === '1'); } catch (e) { return false; }
  }

  /* ── 1. Dawn family ──────────────────────────────────────────────────────
     The theme names its own sections. We never do.

     A getSectionsToRender() entry is {id, section?, selector?}. Dawn's own
     renderer treats them as: when `section` is set, `id` is a DOM element id to
     look inside; when it is not, `id` is BOTH the section name and the element
     id. `selector` picks the node to swap inside that scope, defaulting to the
     section wrapper. Reproducing that faithfully is what makes forks work. */
  var DAWN_HOSTS = 'cart-drawer,cart-items,cart-drawer-items,cart-notification';

  function dawnTargets() {
    var out = [];
    var seen = {};
    var hosts;
    try { hosts = document.querySelectorAll(DAWN_HOSTS); } catch (e) { return out; }

    for (var i = 0; i < hosts.length; i++) {
      var entries;
      try {
        entries = hosts[i].getSectionsToRender && hosts[i].getSectionsToRender();
      } catch (e) { continue; }
      if (!entries || !entries.length) continue;

      for (var j = 0; j < entries.length; j++) {
        var entry = entries[j];
        if (!entry) continue;
        var name = entry.section || entry.id;
        if (!name || seen[name]) continue;

        var scope = entry.section ? document.getElementById(entry.id) : document;
        if (!scope) continue;

        var mount;
        if (entry.selector) {
          mount = scope.querySelector(entry.selector) || (entry.section ? scope : null);
        } else {
          mount = document.getElementById(entry.id);
        }
        if (!mount) continue;

        seen[name] = true;
        out.push({ name: name, mount: mount, selector: entry.selector || '.shopify-section' });
      }
    }
    return out;
  }

  /* A theme with no getSectionsToRender() can still be served, because a
     section's own wrapper carries its real id: <div id="shopify-section-{id}">.
     Walking up from a cart node to that wrapper reads the id off the DOM rather
     than guessing it — which is exactly the step the old code was missing. */
  var CART_NODES = [
    'cart-drawer', 'cart-drawer-component', 'cart-items', 'cart-items-component',
    'cart-drawer-items', 'cart-notification', 'cart-icon-bubble',
    '#CartDrawer', '#cart-drawer', '#CartContainer',
    '.cart-drawer', '.drawer--cart', '.js-cart-drawer',
    '[is="cart-drawer"]', '[data-cart-drawer]'
  ].join(',');

  function wrapperTargets(taken) {
    var out = [];
    var nodes;
    try { nodes = document.querySelectorAll(CART_NODES); } catch (e) { return out; }

    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      if (!node.closest) continue;
      var wrap = node.closest('[id^="shopify-section-"]');
      if (!wrap || !wrap.id) continue;
      var name = wrap.id.slice('shopify-section-'.length);
      if (!name || taken[name]) continue;
      taken[name] = true;
      /* The response for a section is its wrapper markup, so the selector that
         extracts it is the wrapper class, and the mount is this wrapper. */
      out.push({ name: name, mount: wrap, selector: '.shopify-section' });
    }
    return out;
  }

  function graft(targets, sections) {
    if (!targets.length || !sections) return 0;
    var done = 0;
    for (var i = 0; i < targets.length; i++) {
      var t = targets[i];
      var html = sections[t.name];
      /* A section that does not exist comes back null inside an HTTP 200. The
         old code could not distinguish that from success; counting grafts is
         what lets sync() report honestly. */
      if (!html) continue;
      var parsed = null;
      try {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        /* Three ways in, because one is not enough. The theme's own selector
           first (a fork may extract an inner node). Then the section wrapper
           BY ID, which is the only identifier Shopify guarantees — Dawn's
           default '.shopify-section' is a CLASS, and a theme that renders its
           own wrapper without it would silently graft nothing. Finally the
           parsed body, so a fragment response is not thrown away. */
        parsed = doc.querySelector(t.selector) ||
                 doc.getElementById('shopify-section-' + t.name) ||
                 (doc.body && doc.body.firstElementChild ? doc.body : null);
      } catch (e) { continue; }
      if (!parsed) continue;
      while (t.mount.firstChild) t.mount.removeChild(t.mount.firstChild);
      while (parsed.firstChild) t.mount.appendChild(parsed.firstChild);
      done++;
    }
    return done;
  }

  /* ── 2. Horizon family ───────────────────────────────────────────────────
     `promise` is the load-bearing part, not the name. Every Horizon listener
     reads event.promise?.then(...), so an event without one is discarded in
     silence. cart-drawer-component additionally reads event.action to decide
     whether to auto-open, and only 'add' opens it. */
  function horizonNotify(cart) {
    var ev;
    try {
      ev = new CustomEvent('shopify:cart:lines-update', { bubbles: true, cancelable: false });
    } catch (e) { return false; }

    var summary = { totalQuantity: cart && typeof cart.item_count === 'number' ? cart.item_count : undefined };
    if (cart && cart.token) summary.id = cart.token;

    try {
      ev.action = 'add';
      ev.context = 'product';
      /* Resolve, never reject: a rejected promise reaches the themes' .catch
         and logs a console warning on the merchant's storefront for something
         that is not their problem. */
      ev.promise = Promise.resolve({
        cart: summary,
        detail: {
          itemCount: summary.totalQuantity,
          source: 'modulo',
          /* items lets Horizon's product-form update its own quantity label
             without another fetch; sections is the drawer markup from the
             /cart.js read, keyed by the id read off cart-items-component, so
             the theme morphs it in place (hydration mode, its own morph) with
             no second fetch -- the exact shape of the theme's own add. A key
             it does not find falls through to its own renderSection(). */
          items: cart && cart.items ? cart.items : undefined,
          sections: cart && cart.sections ? cart.sections : undefined
        }
      });
    } catch (e) { /* a frozen Event impl: send it bare rather than not at all */ }

    document.dispatchEvent(ev);
    return true;
  }

  /* ── 3. Legacy vocabulary ────────────────────────────────────────────────
     Kept exactly as shipped so no store that works today stops working. The
     cart:update detail is now the shape Shopify's own runtime emits
     ({resource, sourceId, data:{source, itemCount}}) rather than the one we
     invented, which was missing two of the four keys. */
  function legacyNotify(cart) {
    var count = cart && typeof cart.item_count === 'number' ? cart.item_count : null;

    try { document.dispatchEvent(new CustomEvent('cart:refresh')); } catch (e) {}
    try { document.dispatchEvent(new CustomEvent('cart:updated')); } catch (e) {}
    try {
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: null,
          sourceId: 'modulo',
          data: { source: 'modulo', itemCount: count }
        }
      }));
    } catch (e) {}

    if (window.Shopify && window.Shopify.PubSub) {
      try { window.Shopify.PubSub.publish('cart-update', { source: 'modulo', cartData: cart || undefined }); } catch (e) {}
    }
    /* Dawn exposes publish() on window in some builds; PUB_SUB_EVENTS.cartUpdate
       is its own constant, so read it rather than assuming the string. */
    if (typeof window.publish === 'function') {
      try {
        window.publish(
          (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) || 'cart-update',
          { source: 'modulo', cartData: cart || undefined }
        );
      } catch (e) {}
    }
  }

  /* ── 4. The read ─────────────────────────────────────────────────────────
     One GET returns both the true cart and the theme's own section HTML.

     itemCount is read here and never summed from the /cart/add.js response:
     that response is documented as "the line items associated with the added
     items" and carries no total, so summing it rewrites a shopper holding 3
     items down to the 1 they just added. */
  function readCart(targets) {
    var url = root() + 'cart.js';
    if (targets.length) {
      /* Documented cap is five sections per request. */
      var names = [];
      for (var i = 0; i < targets.length && i < 5; i++) names.push(targets[i].name);
      url += '?sections=' + encodeURIComponent(names.join(','));
    }
    return fetch(url, { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  /* ── 5. Sync ─────────────────────────────────────────────────────────────
     Returns a promise for { cart, grafted } so a caller can tell what actually
     happened. It never rejects: a failed refresh must not turn a successful
     add into an error in the shopper's face. */
  function sync() {
    var targets = dawnTargets();
    var taken = {};
    for (var i = 0; i < targets.length; i++) taken[targets[i].name] = true;
    targets = targets.concat(wrapperTargets(taken));

    var hz = isHorizonCart();
    /* A wrapper that hosts Horizon's components is the THEME's to morph: it
       gets the markup through detail.sections instead. Grafting it ourselves
       would replace live custom elements (theme-drawer, cart-items-component)
       that its morph deliberately preserves. Dawn-family targets are still
       grafted, because Dawn has no listener that would do it for us. */
    var ours = [];
    for (var j = 0; j < targets.length; j++) {
      if (!(hz && hostsHorizon(targets[j].mount))) ours.push(targets[j]);
    }

    return readCart(targets).then(function (cart) {
      var grafted = graft(ours, cart && cart.sections);
      horizonNotify(cart);
      legacyNotify(cart);
      var names = [];
      for (var k = 0; k < targets.length; k++) names.push(targets[k].name);
      return { cart: cart, grafted: grafted, horizon: hz, sections: names };
    });
  }

  /* ── 6. Open ─────────────────────────────────────────────────────────────
     Here the first-party action IS the right tool and costs nothing: it does
     not mutate, and a theme that has configured openCart (Horizon does) knows
     how to show its own cart far better than a selector list does.

     But it is gated on there being something to open. Horizon's configured
     handler ends `window.location.href = Theme.routes.cart_url`, and Shopify's
     own default does the same — so on a store whose cart type is "page",
     calling openCart() after every quick-add would NAVIGATE THE SHOPPER AWAY
     from the grid they were browsing. That is a worse bug than the one being
     fixed. If no in-page cart surface exists we do nothing; the shopper's own
     click then lands on a freshly rendered /cart. */
  var OPENABLE = [
    'cart-drawer-component', 'cart-drawer', 'cart-notification',
    'theme-drawer#cart-drawer', '#CartDrawer', '#cart-drawer',
    '.cart-drawer', '.drawer--cart', '[is="cart-drawer"]', '[data-cart-drawer]'
  ].join(',');

  function findOpenable() {
    try { return document.querySelector(OPENABLE); } catch (e) { return null; }
  }

  /* A theme-CONFIGURED Shopify.actions.openCart knows how to show its own
     cart. The DEFAULT handler is a different animal: it tries .open() on
     cart-drawer-component / cart-drawer and otherwise NAVIGATES to /cart, so
     it is never called from here -- a store whose drawer is #CartDrawer would
     lose the page the shopper was on. isDefault() is the runtime's own flag;
     a runtime without it is treated as default, i.e. never called. */
  function themeOpenCart() {
    var a = window.Shopify && window.Shopify.actions, oc = a && a.openCart;
    if (typeof oc !== 'function' || typeof oc.isDefault !== 'function' || oc.isDefault()) return false;
    try {
      var r = oc();
      if (r && typeof r.catch === 'function') r.catch(function () {});
      return true;
    } catch (e) { return false; }
  }

  var lastOpen = null;

  function open() {
    lastOpen = null;
    var node = findOpenable();
    if (!node) return false;

    /* Horizon: the drawer belongs to the theme. Our event carried
       action:'add', and cart-drawer-component opens on it exactly when the
       merchant's auto-open setting says so. Forcing it open here would
       override that setting -- and the old code's class-poke on
       <cart-drawer-component> never opened anything anyway (measured). */
    if (isHorizonCart() && document.querySelector('cart-drawer-component')) { lastOpen = 'theme'; return false; }

    if (themeOpenCart()) { lastOpen = 'actions'; return true; }

    /* Themes vary. Modern Dawn exposes .open(); some forks .openDrawer();
       older Dawn/Brooklyn use a <details open>. `open` as a PROPERTY setter is
       checked separately because a typeof === 'function' test silently skips
       it. lastOpen records WHICH one answered: a class toggle is a guess, not
       a reach, and the diagnostics need to tell the two apart. */
    if (typeof node.open === 'function') { try { node.open(); lastOpen = 'method'; return true; } catch (e) {} }
    if (typeof node.show === 'function') { try { node.show(); lastOpen = 'method'; return true; } catch (e) {} }
    if (typeof node.openDrawer === 'function') { try { node.openDrawer(); lastOpen = 'method'; return true; } catch (e) {} }
    if ('open' in node && typeof node.open !== 'function') { try { node.open = true; lastOpen = 'setter'; return true; } catch (e) {} }
    if (node.tagName === 'DETAILS') { node.setAttribute('open', ''); lastOpen = 'details'; return true; }

    node.classList.add('active', 'is-active', 'open', 'is-open');
    node.setAttribute('aria-hidden', 'false');
    lastOpen = 'class';
    return true;
  }

  /* ── 6b. Diagnostics ─────────────────────────────────────────────────────
     Every outcome lands on ModuloCart.last (and in the console when
     window.MODULO_DEBUG or localStorage.modulo_debug='1' is set, the same
     switch the bundle block uses). An outcome that reached NO surface we can
     vouch for is beaconed ONCE per session to the app's storefront-error
     proxy as kind:'diagnostic' -- the server logs it at warn (Sentry, no
     Discord page) with the theme's name and version -- so a theme this engine
     does not know how to talk to shows up in our logs before a merchant has
     to write in. Three merchants wrote in first. "Reached" means a Horizon
     component is on the page, a section was grafted, or the drawer answered a
     real open API; a class toggle cannot be verified from here. */
  function record(res, via) {
    var t = (window.Shopify && window.Shopify.theme) || {};
    var o = {
      theme: t.schema_name || null,
      version: t.schema_version || null,
      horizon: !!(res && res.horizon),
      grafted: (res && res.grafted) || 0,
      sections: (res && res.sections) || [],
      opened: via || null,
      count: res && res.cart && typeof res.cart.item_count === 'number' ? res.cart.item_count : null
    };
    o.reached = o.horizon || o.grafted > 0 || via === 'method' || via === 'setter' || via === 'details' || via === 'actions' || via === 'theme';
    window.ModuloCart.last = o;
    if (debugOn()) { try { console.log('[modulo cart] sync', o); } catch (e) {} }
    if (o.reached) return o;
    try {
      if (sessionStorage.getItem('modulo_cs_reported')) return o;
      sessionStorage.setItem('modulo_cs_reported', '1');
    } catch (e) {}
    try {
      var body = JSON.stringify({
        msg: 'cart sync: no verified theme surface',
        kind: 'diagnostic',
        url: location.origin + location.pathname,
        x: o
      });
      if (navigator.sendBeacon) navigator.sendBeacon('/apps/modulo/storefront-error', body);
      else fetch('/apps/modulo/storefront-error', { method: 'POST', body: body, keepalive: true }).catch(function () {});
    } catch (e) {}
    return o;
  }

  /* Deliberately NOT here: clicking a[href$="/cart"] as a last resort. That is
     a navigation dressed up as an "open", and it throws away the page the
     shopper was on. */

  /* ── 7. Public surface ───────────────────────────────────────────────────
     after() is what a section calls once its add has succeeded. */
  window.ModuloCart = {
    sync: sync,
    open: open,
    last: null,
    after: function (opts) {
      var wantOpen = !opts || opts.open !== false;
      return sync().then(function (res) {
        if (wantOpen) open();
        record(res, wantOpen ? lastOpen : null);
        return res;
      });
    }
  };
})();
